/**
 * Shared keyboard, focus, dialog, and announcement helpers for custom sections.
 * The public API is intentionally small so section scripts can use it safely.
 */
(function () {
  'use strict';

  const focusableSelector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const dialogState = new WeakMap();

  function visibleFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
    });
  }

  function isolateDialog(dialog) {
    const changed = [];
    let branch = dialog;
    let parent = dialog.parentElement;

    while (parent && parent !== document.body) {
      Array.from(parent.children).forEach((sibling) => {
        if (sibling === branch || sibling.matches('script, style')) return;
        changed.push({ element: sibling, wasInert: sibling.inert });
        sibling.inert = true;
      });
      branch = parent;
      parent = parent.parentElement;
    }

    if (parent === document.body) {
      Array.from(document.body.children).forEach((sibling) => {
        if (sibling === branch || sibling.matches('script, style')) return;
        changed.push({ element: sibling, wasInert: sibling.inert });
        sibling.inert = true;
      });
    }

    return changed;
  }

  function openDialog(dialog, trigger, initialFocus) {
    if (!dialog || dialogState.has(dialog)) return;

    const returnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement;
    dialog.hidden = false;
    dialog.setAttribute('aria-hidden', 'false');
    document.body.classList.add('therasage-dialog-open');

    const isolated = isolateDialog(dialog);
    const keydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        const closeRequest = new CustomEvent('therasage:dialog-close-request', { cancelable: true });
        if (dialog.dispatchEvent(closeRequest)) closeDialog(dialog);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = visibleFocusable(dialog);
      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', keydown);
    dialogState.set(dialog, { returnFocus, isolated, keydown });

    function focusInitialTarget() {
      const requestedTarget = initialFocus instanceof HTMLElement && dialog.contains(initialFocus) ? initialFocus : null;
      const target = requestedTarget || visibleFocusable(dialog)[0] || dialog;

      if (target === dialog && !dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });

      return dialog.contains(document.activeElement);
    }

    window.requestAnimationFrame(() => {
      if (!focusInitialTarget()) window.requestAnimationFrame(focusInitialTarget);
    });
  }

  function closeDialog(dialog) {
    const state = dialogState.get(dialog);
    if (!dialog) return;

    dialog.hidden = true;
    dialog.setAttribute('aria-hidden', 'true');

    if (state) {
      dialog.removeEventListener('keydown', state.keydown);
      state.isolated.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      dialogState.delete(dialog);
      if (state.returnFocus instanceof HTMLElement && state.returnFocus.isConnected) {
        state.returnFocus.focus();
      }
    }

    if (!document.querySelector('[role="dialog"][aria-hidden="false"]')) {
      document.body.classList.remove('therasage-dialog-open');
    }
  }

  function announce(message, priority) {
    const region = document.getElementById(priority === 'assertive' ? 'TherasageAlertRegion' : 'TherasageStatusRegion');
    if (!region) return;
    region.textContent = '';
    window.requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  const pauseIcon = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="currentColor"><path d="M3.5 2.5h3v11h-3zm6 0h3v11h-3z"/></svg>';
  const playIcon = '<svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5z"/></svg>';

  function setMotionButtonState(button, paused, pauseLabel, playLabel) {
    if (!(button instanceof HTMLButtonElement)) return;
    const label = paused ? playLabel : pauseLabel;
    button.innerHTML = paused ? playIcon : pauseIcon;
    button.setAttribute('aria-pressed', String(paused));
    button.setAttribute('aria-label', label);
    button.title = label;
  }

  window.TherasageA11y = {
    closeDialog,
    openDialog,
    announce,
    focusableSelector,
    setMotionButtonState,
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)')
  };

  document.querySelectorAll('.ds-marquee').forEach((marquee) => {
    if (marquee.dataset.a11yReady === 'true') return;
    marquee.dataset.a11yReady = 'true';
    marquee.setAttribute('role', 'region');
    marquee.setAttribute('aria-label', 'Featured publications and partners');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'ds-marquee__motion';
    setMotionButtonState(button, false, 'Pause logo animation', 'Play logo animation');
    marquee.prepend(button);

    button.addEventListener('click', () => {
      const paused = marquee.classList.toggle('is-paused');
      setMotionButtonState(button, paused, 'Pause logo animation', 'Play logo animation');
    });
  });

  let lastCalendlyTrigger = null;
  let activeCalendlyDialog = null;
  let generatedId = 0;
  let enhancementFrame = null;

  function uniqueId(prefix) {
    generatedId += 1;
    return `${prefix}-${generatedId}`;
  }

  function enhanceCalendlyOverlay(overlay, attempt) {
    if (!(overlay instanceof HTMLElement) || overlay.dataset.therasageA11yReady === 'true') return;

    const close = overlay.querySelector('.calendly-popup-close');
    if (!close && (attempt || 0) < 20) {
      window.setTimeout(() => enhanceCalendlyOverlay(overlay, (attempt || 0) + 1), 50);
      return;
    }

    overlay.dataset.therasageA11yReady = 'true';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Schedule a free wellness consultation');

    if (close instanceof HTMLElement) {
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
      close.setAttribute('aria-label', 'Close scheduling dialog');
      close.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        close.click();
      });
      close.addEventListener('click', () => {
        window.setTimeout(() => {
          closeDialog(overlay);
          if (lastCalendlyTrigger) lastCalendlyTrigger.setAttribute('aria-expanded', 'false');
          if (activeCalendlyDialog === overlay) activeCalendlyDialog = null;
        }, 0);
      }, { capture: true });
    }

    overlay.addEventListener('therasage:dialog-close-request', (event) => {
      event.preventDefault();
      if (window.Calendly && typeof window.Calendly.closePopupWidget === 'function') {
        window.Calendly.closePopupWidget();
      } else if (close instanceof HTMLElement) {
        close.click();
      }
      closeDialog(overlay);
      if (lastCalendlyTrigger) lastCalendlyTrigger.setAttribute('aria-expanded', 'false');
      activeCalendlyDialog = null;
    });

    activeCalendlyDialog = overlay;
    if (lastCalendlyTrigger) lastCalendlyTrigger.setAttribute('aria-expanded', 'true');
    openDialog(overlay, lastCalendlyTrigger, close);
  }

  function findNearbyLabel(field) {
    let scope = field.parentElement;

    for (let depth = 0; scope && depth < 4; depth += 1) {
      const controls = scope.querySelectorAll('input, select, textarea');
      const labels = scope.querySelectorAll('label');
      if (controls.length === 1 && labels.length === 1) return labels[0];
      if (controls.length > 1) return null;
      scope = scope.parentElement;
    }

    return null;
  }

  function enhanceShogunForms(root) {
    const fields = root.querySelectorAll([
      'input.shogun-form-text-input',
      '.shogun-form-text-input input',
      'textarea.shogun-form-textarea',
      '.shogun-form-textarea textarea',
      '.shogun-form select',
      'form[class*="shogun"] input:not([type="hidden"]):not([type="submit"]):not([type="button"])',
      'form[class*="shogun"] textarea',
      'form[class*="shogun"] select'
    ].join(','));

    fields.forEach((field) => {
      if (!(field instanceof HTMLElement)) return;

      if (field.dataset.therasageA11yReady !== 'true') {
        const label = field.labels && field.labels.length ? field.labels[0] : findNearbyLabel(field);
        if (label instanceof HTMLLabelElement) {
          if (!field.id) field.id = uniqueId('TherasageShogunField');
          label.htmlFor = field.id;
        }
        field.dataset.therasageA11yReady = 'true';
      }

      let scope = field.parentElement;
      for (let depth = 0; scope && depth < 3; depth += 1) {
        const error = scope.querySelector('[class*="error"], [role="alert"]');
        if (error instanceof HTMLElement && error.textContent.trim()) {
          if (!error.id) error.id = uniqueId('TherasageShogunError');
          const descriptions = new Set((field.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
          descriptions.add(error.id);
          field.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
          error.setAttribute('role', 'alert');
          error.setAttribute('aria-live', 'assertive');
          break;
        }
        scope = scope.parentElement;
      }
    });

    root.querySelectorAll('.shogun-form-success-message, [class*="shogun"][class*="success"]').forEach((message) => {
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
    });
  }

  function enhanceInstagramFeed(feed) {
    if (!(feed instanceof HTMLElement)) return;

    const viewport = feed.querySelector('.ifs-viewport');
    const container = feed.querySelector('.ifs-container');
    const slides = Array.from(feed.querySelectorAll('.ifs-slide'));
    if (!(viewport instanceof HTMLElement) || !(container instanceof HTMLElement) || !slides.length) return;

    feed.classList.remove('therasage-instagram-manual');
    feed.classList.add('therasage-instagram-enhanced');
    feed.setAttribute('role', 'region');
    feed.setAttribute('aria-label', 'Instagram posts from Therasage');
    viewport.setAttribute('role', 'group');
    viewport.setAttribute('aria-label', 'Instagram post carousel');

    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Instagram post ${index + 1} of ${slides.length}`);
    });

    let button = feed.querySelector('.therasage-instagram-motion');
    if (button) return;

    button = document.createElement('button');
    button.type = 'button';
    button.className = 'therasage-instagram-motion';
    feed.append(button);

    let paused = window.TherasageA11y.prefersReducedMotion.matches;
    const applyPausedState = () => {
      if (paused) {
        const transform = window.getComputedStyle(container).transform;
        feed.style.setProperty('--therasage-instagram-paused-transform', transform === 'none' ? (container.style.transform || 'none') : transform);
        feed.classList.add('is-paused');
      } else {
        feed.classList.remove('is-paused');
        feed.style.removeProperty('--therasage-instagram-paused-transform');
      }
      setMotionButtonState(button, paused, 'Pause Instagram carousel', 'Play Instagram carousel');
    };

    button.addEventListener('click', () => {
      paused = !paused;
      applyPausedState();
    });

    window.TherasageA11y.prefersReducedMotion.addEventListener?.('change', (event) => {
      if (event.matches) paused = true;
      applyPausedState();
    });

    window.requestAnimationFrame(applyPausedState);
  }

  function runDynamicEnhancements() {
    enhancementFrame = null;
    document.querySelectorAll('.therasage-calendly-btn').forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      if (!trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'false');
    });
    enhanceShogunForms(document);
    const feed = document.getElementById('instafeed');
    if (feed) enhanceInstagramFeed(feed);
    document.querySelectorAll('.calendly-overlay').forEach((overlay) => enhanceCalendlyOverlay(overlay, 0));
  }

  function scheduleDynamicEnhancements() {
    if (enhancementFrame !== null) return;
    enhancementFrame = window.requestAnimationFrame(runDynamicEnhancements);
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.therasage-calendly-btn');
    if (trigger instanceof HTMLElement) lastCalendlyTrigger = trigger;
  }, true);

  const dynamicObserver = new MutationObserver((mutations) => {
    if (activeCalendlyDialog && !activeCalendlyDialog.isConnected) {
      closeDialog(activeCalendlyDialog);
      if (lastCalendlyTrigger) lastCalendlyTrigger.setAttribute('aria-expanded', 'false');
      activeCalendlyDialog = null;
    }

    if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
      scheduleDynamicEnhancements();
    }
  });

  dynamicObserver.observe(document.body, { childList: true, subtree: true });
  runDynamicEnhancements();

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.querySelectorAll('video[autoplay], video[data-therasage-autoplay]').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }
})();
